import { Button, InputNumber } from "antd";
import MinusIcon from "icons/MinusIcon";
import PlusIcon from "icons/PlusIcon";
import { FC, useState, useEffect } from "react";

interface INInputNumber {
  value: number | string;
  onChange?: (value: number) => void;
  disabled?: boolean;
}

const NInputNumber: FC<INInputNumber> = ({
  value,
  onChange,
  disabled = false,
}) => {
  // Local state to manage the display value
  const [displayValue, setDisplayValue] = useState<number | string | null>(
    value
  );
  const onPlus = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation(); // Stop event from bubbling up to parent elements
    if (!disabled && onChange) {
      // Always ensure we pass a number
      onChange(value === "" ? 1 : Number(value) + 1);
    }
  };

  const onMinus = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation(); // Stop event from bubbling up to parent elements
    const numericValue = value === "" ? 0 : Number(value);
    if (!disabled && numericValue > 0 && onChange) {
      // Always ensure we pass a number
      onChange(numericValue - 1);
    }
  };

  // We'll handle input changes directly in handleInputNumberChange

  const onClickInput = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation(); // Stop event from bubbling up to parent elements

    // Only clear the display value, not the actual value
    setDisplayValue("");
  };

  const onBlurInput = () => {
    // If the display value is empty or invalid, reset it to the actual value or 1
    if (
      displayValue === "" ||
      displayValue === null ||
      displayValue === undefined ||
      Number(displayValue) === 0
    ) {
      // If the actual value is also empty/invalid, set to 1
      if (
        onChange &&
        (value === "" || value === 0 || value === null || value === undefined)
      ) {
        // Always ensure we pass a number
        onChange(1);
        setDisplayValue(1);
      } else {
        // Otherwise reset to the actual value
        setDisplayValue(value);
      }
    }
  };

  // Sync the display value with the actual value when the prop changes
  useEffect(() => {
    setDisplayValue(value);
  }, [value]);

  // Handler for the InputNumber onChange event
  const handleInputNumberChange = (v: number | null) => {
    // Update both the display value and notify the parent
    setDisplayValue(v);
    if (onChange && v !== null) {
      // Always ensure we pass a number
      onChange(v);
    }
  };

  return (
    <InputNumber
      className="w-[110px] narin-input-number text-primary"
      controls={false}
      disabled={disabled}
      value={
        typeof displayValue === "string" && displayValue !== ""
          ? Number(displayValue)
          : displayValue === ""
          ? null
          : displayValue
      }
      min={0}
      onClick={disabled ? undefined : onClickInput}
      onChange={disabled ? undefined : handleInputNumberChange}
      onBlur={disabled ? undefined : onBlurInput}
      // Stop propagation for the entire component
      onMouseDown={(e) => e.stopPropagation()}
      addonAfter={
        <Button
          onClick={onPlus}
          type="text"
          size="small"
          disabled={disabled}
          className="text-primary text-2xl w-7"
        >
          <PlusIcon />
        </Button>
      }
      addonBefore={
        <Button
          onClick={onMinus}
          type="text"
          size="small"
          disabled={disabled}
          className="text-primary text-2xl w-7"
        >
          <MinusIcon />
        </Button>
      }
    />
  );
};

export default NInputNumber;
